// src/features/pages/report/components/DateFilter.jsx
import React, { useState } from 'react';
import {
    Box,
    Button,
    Popover,
    Stack,
    Typography,
    IconButton,
    TextField,
    Grid
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    Clear as ClearIcon
} from '@mui/icons-material';

const DateFilter = ({ startDate, endDate, onDateChange, onClear }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [tempStartDate, setTempStartDate] = useState('');
    const [tempEndDate, setTempEndDate] = useState('');

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        setTempStartDate(startDate ? formatDateForInput(startDate) : '');
        setTempEndDate(endDate ? formatDateForInput(endDate) : '');
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleApply = () => {
        const start = tempStartDate ? new Date(tempStartDate + 'T00:00:00') : null;
        const end = tempEndDate ? new Date(tempEndDate + 'T23:59:59') : null;

        if (start && end && start > end) {
            alert('Tanggal mulai harus lebih kecil dari tanggal akhir');
            return;
        }

        onDateChange(start, end);
        handleClose();
    };

    const handleReset = () => {
        setTempStartDate('');
        setTempEndDate('');
        onDateChange(null, null);
        handleClose();
        if (onClear) onClear();
    };

    const open = Boolean(anchorEl);
    const popoverId = open ? 'date-filter-popover' : undefined;

    const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleDateString('id-ID', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatDateForInput = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().split('T')[0];
    };

    const hasDateFilter = startDate || endDate;

    return (
        <Box>
            <Button
                variant={hasDateFilter ? "contained" : "outlined"}
                startIcon={<CalendarIcon />}
                onClick={handleClick}
                size="small"
                color={hasDateFilter ? "primary" : "inherit"}
                sx={{
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    ...(hasDateFilter && {
                        bgcolor: '#1976d2',
                        color: 'white',
                        '&:hover': {
                            bgcolor: '#1565c0'
                        }
                    })
                }}
            >
                {hasDateFilter ? (
                    <>
                        {formatDate(startDate)} - {formatDate(endDate)}
                        <IconButton
                            size="small"
                            sx={{ ml: 0.5, p: 0, color: 'inherit' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleReset();
                            }}
                        >
                            <ClearIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </>
                ) : (
                    'Tanggal Cetak'
                )}
            </Button>

            <Popover
                id={popoverId}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        p: 3,
                        minWidth: 380,
                        maxWidth: 450,
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
                    }
                }}
                slotProps={{
                    paper: {
                        style: { padding: '24px' }, // Inline style approach
                    },
                }}
            >
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Filter Tanggal Cetak
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                    Pilih rentang tanggal untuk memfilter data
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Tanggal Mulai"
                            type="date"
                            value={tempStartDate}
                            onChange={(e) => setTempStartDate(e.target.value)}
                            fullWidth
                            size="small"
                            slotProps={{
                                inputLabel: { shrink: true }
                            }} 
                            inputProps={{
                                max: tempEndDate || undefined
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    borderRadius: 1.5
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Tanggal Akhir"
                            type="date"
                            value={tempEndDate}
                            onChange={(e) => setTempEndDate(e.target.value)}
                            fullWidth
                            size="small"
                            slotProps={{
                                inputLabel: { shrink: true }
                            }}
                            inputProps={{
                                min: tempStartDate || undefined
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    borderRadius: 1.5
                                }
                            }}
                        />
                    </Grid>
                </Grid>

                <Box display="flex" justifyContent="flex-end" gap={1.5} sx={{ mt: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={handleReset}
                        size="small"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            borderRadius: 1.5
                        }}
                    >
                        RESET
                    </Button>
                    &nbsp;
                    <Button
                        variant="contained"
                        onClick={handleApply}
                        size="small"
                        disabled={!tempStartDate && !tempEndDate}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            borderRadius: 1.5,
                            bgcolor: '#1976d2',
                            '&:hover': {
                                bgcolor: '#1565c0'
                            }
                        }}
                    >
                        TERAPKAN
                    </Button>
                </Box>
            </Popover>
        </Box>
    );
};

export default DateFilter;